import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridItemChangeEvent,
} from '@progress/kendo-react-grid'
import { Upload, UploadFileInfo, UploadOnAddEvent } from '@progress/kendo-react-upload'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { ClientDocumentModel, DocumentStatusId } from 'api/Documents/Types/ClientDocument'
import { FlexBox } from 'components/Box'
import { Modal } from 'components/CustomModal'
import MoreButton from 'components/MoreButton/MoreButton'
import { useAppDispatch } from 'store'
import { uploadClientDocumentsAction } from 'store/documents/documents.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { DocumentApiClient } from 'utils/api/documentApi'
import { toBase64 } from 'utils/file.helpers'
import { ImageTypePattern } from 'utils/formValidation'
import { removeSpecialCharacterFromText } from 'utils/string.utils'

import {
  ColumnCategoryDropdown,
  ColumnTypeDropdown,
  StatusTypeDropdown,
} from './CategoryTypeDropDown'

interface FileInfo extends UploadFileInfo {
  type?: { value: number; text: string }
  category?: { value: number; text: string }
  discontinued: boolean
  inEdit: boolean
  expiration?: string
  number?: string
  personalIdNumber?: string
  country?: { value: string; text: string }
  getRawFile?: () => File
  statusId?: { value: number; text: string }
}

interface FileInfoUpload extends Omit<FileInfo, 'getRawFile' | 'category' | 'type'> {
  category: { value: number; text: string }
  type: { value: number; text: string }
  getRawFile: () => File
}

interface DispatchProps {
  saveClientDocuments: (files: ClientDocumentModel[]) => void
}

const useMapDispatchToProps = (): DispatchProps => {
  const dispatch = useAppDispatch()

  const documentApiClient = useApiClient(DocumentApiClient)
  return {
    saveClientDocuments: (files: ClientDocumentModel[]) =>
      dispatch(uploadClientDocumentsAction(files, documentApiClient)),
  }
}

type Props = {
  client: ClientDetailedDto
  forceRefresh: () => void
  closeModal(): void
}

const FilenameInputCell: React.FC<GridCellProps> = (props) => {
  const value = props.field ? (props.dataItem[props.field] ?? '') : ''

  if (!props.dataItem.inEdit) {
    return <td>{value}</td>
  }

  return (
    <td>
      <Input
        value={value}
        onChange={(event) => {
          props.onChange?.({
            dataIndex: props.dataIndex,
            dataItem: props.dataItem,
            field: props.field,
            value: event.target.value,
            syntheticEvent: event,
          })
        }}
      />
    </td>
  )
}

export const AddDocumentModal: React.FC<Props> = (props: Props) => {
  const { saveClientDocuments } = useMapDispatchToProps()
  const [documents, setDocuments] = useState<FileInfo[]>([])
  const [isLoading, setLoading] = useState<boolean>(false)

  const handleDocumentUpload = (event: UploadOnAddEvent): void => {
    const files = [...documents, ...event.newState]
    const edit = files.map((item) => ({ inEdit: true, discontinued: true, ...item }))

    setDocuments(edit)
  }

  const isNotContainsImageType = () => {
    return !!documents.some((x) => !ImageTypePattern.test(x.extension || ''))
  }

  const handleDocumentFilenameChange = (event: GridItemChangeEvent): void => {
    if (event.field) {
      setDocuments((prevDocuments) =>
        prevDocuments.map((document) =>
          document.uid === event.dataItem.uid
            ? { ...document, [event.field as string]: event.value }
            : document
        )
      )
    }
  }

  const handleRemove = (uid: string): void => {
    setDocuments(documents.filter((document: FileInfo) => document.uid !== uid))
  }

  const handleUploadDocuments = async () => {
    setLoading(true)
    const files = await Promise.all(
      documents
        .filter(
          (document: FileInfo): document is FileInfoUpload =>
            !!document?.getRawFile &&
            !!document?.category &&
            !!document?.type &&
            !!document?.discontinued
        )
        .map(async (document): Promise<ClientDocumentModel> => {
          const base64 = await toBase64(document.getRawFile())

          return {
            clientId: props.client.id,
            categoryId: document?.category?.value,
            countryId: document?.country?.value || null,
            number: null,
            personalIdNumber: null,
            typeId: document?.type?.value,
            file: (base64 as string)?.split('base64,')[1],
            filename: removeSpecialCharacterFromText(document.name),
            expirationAt: null,
            statusId: document.statusId?.value === 0 ? null : DocumentStatusId.APPROVED,
          }
        })
    )

    await saveClientDocuments(files)
    setLoading(false)
    props.forceRefresh()
    props.closeModal()
  }

  const isValid = (): boolean => {
    if (documents && !!documents.length) {
      return documents.every((document: FileInfo) => document.type && document.category)
    }

    return false
  }

  return (
    <Modal width={1200} visible={true} onClose={props.closeModal}>
      <Upload
        batch={false}
        autoUpload={false}
        multiple={true}
        showFileList={true}
        onAdd={handleDocumentUpload}
      />
      <Grid
        data={documents}
        editField='inEdit'
        onItemChange={handleDocumentFilenameChange}
        resizable
      >
        <Column width={400} field='name' cell={FilenameInputCell} title='FileName' />
        <Column width={150} field='category' cell={ColumnCategoryDropdown} title='Category' />
        <Column width={150} field='type' cell={ColumnTypeDropdown} title='Type' />
        <Column
          field='Action'
          width={140}
          cell={(documentProps: GridCellProps) => (
            <td>
              <MoreButton
                items={[
                  {
                    name: 'Delete',
                    onClick: () => handleRemove(documentProps.dataItem.uid),
                  },
                ]}
              />
            </td>
          )}
        />
        {isNotContainsImageType() && (
          <Column width={150} field='status' cell={StatusTypeDropdown} title='Status' />
        )}
      </Grid>
      <FlexBox
        style={{ marginTop: 48, marginBottom: 48 }}
        alignItems='center'
        justifyContent='center'
      >
        <Button
          onClick={async () => await handleUploadDocuments()}
          disabled={!isValid() || isLoading}
        >
          Upload
        </Button>
      </FlexBox>
    </Modal>
  )
}
